import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Form, FormActions, FormField } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Input from 'components/inputs/input/Input';
import PageSection from 'components/page/page-section/PageSection';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import { danger } from 'redux-store/reducers/common-reducer';

import { __t, applyTranslations } from 'utils/language';
import { isDefined } from 'utils/helper/common';
import { isAdmin } from 'utils/account';
import {
  LONG_STRING_MAX_LENGTH,
  NAME_MAX_LENGTH,
  isEmail,
  isRequired,
  isUrl,
  maxLength
} from 'utils/validators';
import { BRAND_CATEGORIES } from 'utils/brands';

import './brand-profile-edit-form.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./brand-profile-edit-form.i18n.json'));

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'brand_profile_edit_form';
const MAX_FIELD_LENGTH = 255;

class BrandProfileEditForm extends Component {
  static propTypes = {
    mode: PropTypes.string,
    children: PropTypes.any,
    currentAccount: PropTypes.object,
    roles: PropTypes.arrayOf(PropTypes.string),
    isAgencyBrand: PropTypes.bool,
    brandProfile: PropTypes.object,
    organizationType: PropTypes.string,
    dispatchDanger: PropTypes.func,
    onChange: PropTypes.func,
    onCancel: PropTypes.func,
    onSubmit: PropTypes.func
  };

  static defaultProps = {
    organizationType: 'brand'
  };

  handleFieldChanged = (event) => {
    this.handleDataChange(event.target.name, event.target.value);
  };

  handlePitchChanged = (value) => {
    this.handleDataChange('pitch', value);
  };

  handleCategoryChange = (updatedItems, data, selectedItem) => {
    this.handleDataChange('category', selectedItem?.identifier || updatedItems[0].identifier);
  };

  handleDataChange = (field, value) => {
    const { onChange } = this.props;
    onChange(field, value);
  };

  handleValidationErrors = () => {
    const { dispatchDanger } = this.props;
    dispatchDanger(__t(`${MSG_KEY}.messages.missing_fields`));
  };

  render() {
    const {
      mode,
      brandProfile,
      currentAccount,
      roles,
      isAgencyBrand,
      organizationType,
      children,
      onSubmit,
      onCancel
    } = this.props;

    const canEditBrandName = mode === 'create' || isAdmin(currentAccount, roles);
    const categories = BRAND_CATEGORIES.map(category => ({
      identifier: category,
      value: category,
      isDisabled: false,
      isFocused: false,
      label: __t(`${MSG_KEY}.categories_labels.${category}`),
      isSelected: brandProfile.category === category
    }));

    return (
      <Form
        onSubmit={onSubmit}
        onValidationErrors={this.handleValidationErrors}
      >
        <PageSection>
          <FormField
            label={__t(`${MSG_KEY}.labels.${organizationType}_name`)}
            labelType="highlighted"
            direction="column"
            showRequiredMark
            validators={[
              isRequired(brandProfile.name),
              maxLength(brandProfile.name, NAME_MAX_LENGTH)
            ]}
          >
            <Input
              name="name"
              value={brandProfile.name}
              disabled={!canEditBrandName}
              placeholder={__t(`${MSG_KEY}.placeholders.name`)}
              onChange={this.handleFieldChanged}
              data={{ qa: 'brand-profile-edit-name' }}
            />
          </FormField>

          <FormField
            label={__t(`${MSG_KEY}.labels.email`)}
            labelType="highlighted"
            direction="column"
            showRequiredMark
            tooltipMessage={__t(`${MSG_KEY}.tooltips.email`)}
            validators={[
              isRequired(brandProfile.contactEmail),
              isEmail(brandProfile.contactEmail),
              maxLength(brandProfile.contactEmail, MAX_FIELD_LENGTH)
            ]}
          >
            <Input
              name="contactEmail"
              value={brandProfile.contactEmail}
              placeholder={__t(`${MSG_KEY}.placeholders.email`)}
              onChange={this.handleFieldChanged}
              data={{ qa: 'brand-profile-edit-email' }}
            />
          </FormField>

          <FormField
            label={__t(`${MSG_KEY}.labels.website`)}
            labelType="highlighted"
            direction="column"
            validators={[
              isUrl(brandProfile.url, __t(`${MSG_KEY}.placeholders.website`)),
              maxLength(brandProfile.url, MAX_FIELD_LENGTH)
            ]}
          >
            <Input
              name="url"
              value={brandProfile.url}
              width="wide"
              placeholder={__t(`${MSG_KEY}.placeholders.website`)}
              onChange={this.handleFieldChanged}
              data={{ qa: 'brand-profile-edit-url' }}
            />
          </FormField>

          {organizationType !== 'agency' &&
            <FormField
              showRequiredMark
              label={__t(`${MSG_KEY}.labels.category`)}
              labelType="highlighted"
              direction="column"
              validators={[
                isRequired(brandProfile.category)
              ]}
            >
              <div className="brand-profile-edit-form__dropdown">
                <MixedSelectDropdown
                  name="category"
                  withSelectedInList
                  items={categories}
                  placeholder={__t(`${MSG_KEY}.placeholders.category`)}
                  onItemsToggle={this.handleCategoryChange}
                />
              </div>
            </FormField>
          }

          {
            ['instagram', 'youtube'].map((channelType) => (
              <FormField
                key={`channel-${channelType}`}
                label={__t(`${COMMON_MSG_KEY}.social.${channelType}`)}
                labelType="highlighted"
                direction="column"
                validators={[
                  isUrl(
                    brandProfile[`${channelType}Url`],
                    __t(`${MSG_KEY}.placeholders.${channelType}`)
                  ),
                  maxLength(brandProfile[`${channelType}Url`], MAX_FIELD_LENGTH)
                ]}
              >
                <Input
                  name={`${channelType}Url`}
                  value={brandProfile[`${channelType}Url`]}
                  width="wide"
                  placeholder={__t(`${MSG_KEY}.placeholders.${channelType}`)}
                  onChange={this.handleFieldChanged}
                />
              </FormField>
            ))
          }

          <FormField
            label={__t(`${MSG_KEY}.labels.${organizationType}_pitch`)}
            labelType="highlighted"
            direction="column"
            showValidationIcon={false}
            showRequiredMark={isAgencyBrand}
            validators={[
              isAgencyBrand && isRequired(brandProfile.pitch),
              maxLength(brandProfile.pitch, LONG_STRING_MAX_LENGTH)
            ].filter(isDefined)}
          >
            <RichTextEditor
              key={brandProfile.pitch}
              canEdit
              value={brandProfile.pitch}
              onChange={this.handlePitchChanged}
            />
          </FormField>
          {children}
          <FormActions>
            {
              onCancel &&
                <ActionButton
                  color="tertiary"
                  data={{ qa: 'brand-profile-edit__button--cancel' }}
                  onClick={onCancel}
                >
                  {__t(`${COMMON_MSG_KEY}.action_button.cancel`)}
                </ActionButton>
            }
            <ActionButton
              type="submit"
              data={{ qa: 'brand-profile-edit__button--submit' }}
            >
              {__t(`${COMMON_MSG_KEY}.action_button.save`)}
            </ActionButton>
          </FormActions>
        </PageSection>
      </Form>
    );
  }
}

const mapStateToProps = ({ account }) => ({
  currentAccount: account.currentAccount,
  roles: account.roles
});

const mapDispatchToProps = (dispatch) => ({
  dispatchDanger: message => dispatch(danger(message))
});

export default connect(mapStateToProps, mapDispatchToProps)(BrandProfileEditForm);
